/**
 * EvalAnalysisPage (`/evals/groups/:slug/analysis`) — the benchmark
 * analysis view for one eval group (TRA-831, inspired by the Mercor
 * console).
 *
 * Header stats (tasks, overall score, runs, models, difficulty
 * breakdown), the task score distribution chart (easiest → hardest),
 * and the task ledger. The `?model=` search param filters chart and
 * ledger scores to one model — same URL-param pattern as `?variant=`
 * on the group page.
 */

import { Navigate, Link, useParams, useSearchParams } from 'react-router-dom'
import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'

import { AnalysisLedger } from '@/components/evals/AnalysisLedger'
import { selectedModel } from '@/components/evals/analysis'
import { EvalBreadcrumb } from '@/components/evals/EvalBreadcrumb'
import { formatRelative, formatScore } from '@/components/evals/format'
import { StatRow, type Stat } from '@/components/evals/StatRow'
import { TaskScoreDistribution } from '@/components/evals/TaskScoreDistribution'
import { Button } from '@/components/ui/button'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { useEvalGroupAnalysis } from '@/hooks/useEvals'
import type { EvalGroupAnalysisResponse } from '@/api/generated/model'

function headerStats(analysis: EvalGroupAnalysisResponse): Stat[] {
  const { easy, medium, hard, unscored } = analysis.difficulty
  const difficultyDetail = [
    `${easy.toString()} easy`,
    `${medium.toString()} medium`,
    `${hard.toString()} hard`,
    ...(unscored > 0 ? [`${unscored.toString()} unscored`] : []),
  ].join(' · ')
  return [
    { label: 'Overall score', value: formatScore(analysis.score) },
    {
      label: 'Tasks',
      value: analysis.task_count.toString(),
      detail: <span className="text-xs text-muted-foreground">{difficultyDetail}</span>,
    },
    { label: 'Runs', value: analysis.run_count.toString() },
    { label: 'Models', value: analysis.model_slugs.length.toString() },
    { label: 'Last run', value: formatRelative(analysis.last_run_at) },
  ]
}

/** "All models" + one pill per model; selection lives in `?model=`. */
function ModelFilter({ modelSlugs }: { modelSlugs: string[] }): React.JSX.Element | null {
  const [searchParams, setSearchParams] = useSearchParams()
  const selected = selectedModel(searchParams)
  if (modelSlugs.length < 2) return null

  const select = (model: string | null): void => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous)
        if (model === null) {
          next.delete('model')
        } else {
          next.set('model', model)
        }
        return next
      },
      { replace: true }
    )
  }

  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by model">
      <Button
        variant={selected === null ? 'secondary' : 'ghost'}
        size="sm"
        aria-pressed={selected === null}
        onClick={() => {
          select(null)
        }}
      >
        All models
      </Button>
      {modelSlugs.map((slug) => (
        <Button
          key={slug}
          variant={selected === slug ? 'secondary' : 'ghost'}
          size="sm"
          className="font-mono"
          aria-pressed={selected === slug}
          onClick={() => {
            select(slug)
          }}
        >
          {slug}
        </Button>
      ))}
    </div>
  )
}

export default function EvalAnalysisPage(): React.JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const model = selectedModel(searchParams)
  const { data: analysis, isLoading, isError, error } = useEvalGroupAnalysis(slug)

  return (
    <PageContainer className="space-y-6">
      <EvalBreadcrumb
        crumbs={[
          { label: 'Eval Groups', to: '/evals' },
          { label: analysis?.display_name ?? slug ?? '', to: `/evals/groups/${slug ?? ''}` },
          { label: 'Analysis' },
        ]}
      />

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-60 w-full" />
        </div>
      )}

      {isError && slug !== undefined && getApiErrorStatus(error) === 404 && (
        <Navigate to={`/evals/groups/${slug}`} replace />
      )}
      {isError && getApiErrorStatus(error) !== 404 && (
        <p className="text-sm text-destructive">
          Failed to load analysis: {getApiErrorDetail(error) ?? 'unknown error'}
        </p>
      )}

      {!isLoading && !isError && analysis && (
        <>
          <PageHeader
            title={`${analysis.display_name} · Analysis`}
            description="Score distribution and per-task breakdown across the benchmark."
            actions={
              <Button asChild variant="outline">
                <Link to={`/evals/groups/${analysis.slug}`}>Variant matrix</Link>
              </Button>
            }
          />

          <StatRow stats={headerStats(analysis)} />

          <ModelFilter modelSlugs={analysis.model_slugs} />

          <TaskScoreDistribution
            tasks={analysis.tasks}
            model={model}
            taskPath={(taskSlug) => `/evals/groups/${analysis.slug}/tasks/${taskSlug}`}
          />

          <AnalysisLedger
            tasks={analysis.tasks}
            model={model}
            taskPath={(taskSlug) => `/evals/groups/${analysis.slug}/tasks/${taskSlug}`}
          />
        </>
      )}
    </PageContainer>
  )
}
